import { camelCaseify } from '~/utils'

export const AsyncLoadConcept =
  ({ RestService, pResponseConcept, HandleError }) =>
  async (conceptId) => {
    try {
      const concept = await RestService.get(
        `/api/admin/concepts/${conceptId}`,
        { serializer: 'edit' },
        { timeout: 30000 },
      )

      return pResponseConcept(concept)
    } catch (error) {
      HandleError({ error })
    }
  }

export const AsyncLoadCachedConcepts =
  ({ RestService, pResponseGeneric, HandleError }) =>
  async (conceptIds) => {
    try {
      const concepts = await RestService.get('/api/admin/concepts', {
        concept_ids: conceptIds.join(','),
        cache: '1',
      })

      return pResponseGeneric(concepts)
    } catch (error) {
      HandleError({ error })
    }
  }

export const AsyncLoadCachedFilteredConcepts =
  ({
    RestService,
    pRequestLoadCachedFilteredConcepts,
    pResponseFilteredConcepts,
    HandleError,
  }) =>
  async ({
    clientSetUpTime,
    chefId,
    headquarterId,
    search = '',
    orderType = undefined,
    filters,
    offset,
    limit,
    sortBy,
    sortOrder,
  }) => {
    try {
      const request = pRequestLoadCachedFilteredConcepts({
        clientSetUpTime,
        headquarterId,
        search,
        orderType,
        chefId,
        filters,
        offset,
        limit,
        sortBy,
        sortOrder,
      })

      const response = await RestService.get(
        '/api/admin/concepts/filtered_concepts',
        {
          ...request,
        },
      )

      return pResponseFilteredConcepts(response)
    } catch (error) {
      HandleError({ error })
    }
  }

export const AsyncSearchCachedConcepts =
  ({ RestService, pResponseGeneric, HandleError }) =>
  async (search) => {
    try {
      const concepts = await RestService.get('/api/admin/concepts', {
        ...search,
        cache: '1',
      })

      return pResponseGeneric(concepts)
    } catch (error) {
      HandleError({ error })
    }
  }

export const SearchPopUpConcepts =
  ({ RestService, pResponsePopUpConcepts, HandleError }) =>
  async (search) => {
    try {
      const concepts = await RestService.get('/api/admin/concepts', {
        ...search,
        cache: '1',
        is_approved: '1',
        is_enabled: '1',
        market_type: 'Pop-Up',
      })

      return pResponsePopUpConcepts(concepts)
    } catch (error) {
      HandleError({ error })
    }
  }

export const SearchAvailableChefs =
  ({ RestService, HandleError }) =>
  async ({ clientSetUpTime, headquarterId }) => {
    try {
      const params = {
        date: clientSetUpTime.toString(),
        hqId: headquarterId,
        day: clientSetUpTime.isoWeekday(),
      }
      const cParams = camelCaseify(params)
      const availChefs = await RestService.get(
        '/chefs/schedules/avail',
        cParams,
      )

      return availChefs.map((chef) => chef.id)
    } catch (error) {
      HandleError({ error })

      return []
    }
  }

export const SearchGroupOrderConcepts =
  ({ RestService, pResponseGroupOrderConcepts, HandleError }) =>
  async ({
    search,
    page,
    limit,
    availableOn,
    tagsDietaryPreferenceUnique,
    excludeTagsDietaryPreferenceUnique,
    isUniversal = undefined,
  }) => {
    try {
      const req = {
        search,
        cache: '1',
        is_approved: '1',
        is_enabled: '1',
        market_type: 'Group Order',
        available_on: availableOn,
        tags_dietary_preference_unique: tagsDietaryPreferenceUnique,
        exclude_tags_dietary_preference_unique:
          excludeTagsDietaryPreferenceUnique,
        page,
        limit,
      }
      if (isUniversal !== undefined) {
        req.is_universal = isUniversal
      }

      const concepts = await RestService.get('/api/admin/concepts', req)

      return pResponseGroupOrderConcepts(concepts)
    } catch (error) {
      HandleError({ error })
    }
  }

export const GetGroupOrderConcept =
  ({ RestService, pResponseGroupOrderConcept, HandleError }) =>
  async (id) => {
    try {
      const PAGE_SIZE = 50
      const chunks = []

      const concepts = await RestService.get(`/api/admin/concepts/${id}`, {
        cached: '1',
      })

      for (let i = 0; i < concepts.all_menu_items.length; i += PAGE_SIZE) {
        chunks.push(concepts.all_menu_items.slice(i, i + PAGE_SIZE))
      }
      const menuItemMods = await Promise.all(
        chunks.map((chunk) => {
          const params = new URLSearchParams()
          chunk.forEach((item) => {
            params.append('chefMenuItemIds', item.id)
          })

          return RestService.get(
            `/group-orders/chef-menu-items?${params.toString()}`,
          )
        }),
      )

      return pResponseGroupOrderConcept(concepts, menuItemMods.flat())
    } catch (error) {
      HandleError({ error })
    }
  }

export const GetGroupOrderUniversalConcept =
  ({
    RestService,
    pFilterUniversalMenu,
    pResponseGroupOrderConcept,
    HandleError,
  }) =>
  async (id, universalMenuConfig, excludedItemIds) => {
    try {
      const concept = await RestService.get(
        `/group-orders/universal-menus/${id}`,
      )
      const parsedConcept = pFilterUniversalMenu(
        concept,
        universalMenuConfig,
        excludedItemIds,
      )

      const params = new URLSearchParams()
      parsedConcept.allMenuItems.forEach((item) => {
        params.append('chefMenuItemIds', item.id)
      })
      const menuItemMods = await RestService.get(
        `/group-orders/chef-menu-items?${params.toString()}`,
      )

      return pResponseGroupOrderConcept(parsedConcept, menuItemMods)
    } catch (error) {
      HandleError({ error })
    }
  }

export const AsyncLoadSuggestedConcepts =
  ({ NodeApiService, pResponseGeneric, HandleError }) =>
  async (accountId, headquarterId) => {
    try {
      const params = {
        hide_metrics: '0',
        account_id: accountId,
        headquarter_id: headquarterId,
      }
      const data = await NodeApiService.get(
        'smart_catering_engine/suggestions',
        params,
      )
      const { suggested } = data

      return pResponseGeneric(suggested)
    } catch (error) {
      HandleError({ error })
    }
  }

export const LoadMenuConcepts =
  ({ ChefService, RestService, pResponseConcepts, HandleError }) =>
  async () => {
    const { id: chef_id } = ChefService.getState().chef
    try {
      let concepts = await RestService.get(
        '/api/admin/concepts',
        { chef_id },
        { timeout: 30000 },
      )
      concepts = pResponseConcepts(concepts)
      ChefService.setConcepts(concepts)
    } catch (error) {
      HandleError({ error })
    }
  }

export const DelayedUpdateConcept =
  ({ ChefService, UIService, pStateToReduxConcept }) =>
  (concept) => {
    const call = () => ChefService.setEditConcept(pStateToReduxConcept(concept))
    UIService.Timer.callAfterTimeout(call)
  }

export const EditMenuConcept =
  ({ ChefService, RestService, UIService, pResponseConcept, HandleError }) =>
  async (conceptId) => {
    try {
      const concept = await RestService.get(
        `/api/admin/concepts/${conceptId}`,
        { serializer: 'edit' },
        { timeout: 30000 },
      )
      ChefService.setEditConcept(pResponseConcept(concept))
      UIService.EditMenuConcept.show()
    } catch (error) {
      HandleError({ error })
    }
  }

export const NewMenuConcept =
  ({ ChefService, UIService }) =>
  () => {
    const { chef, editMenuConcept } = ChefService.getState()
    if (editMenuConcept.id) {
      ChefService.clearEditConcept()
    }

    if (chef) {
      ChefService.setEditConcept({ chefId: chef.id })
      UIService.EditMenuConcept.show()
    }
  }

export const DeleteMenuConcept =
  ({ ChefService, RestService, UIService, pResponseConcepts, HandleError }) =>
  async (id, name) => {
    const doDelete = await UIService.ConfirmationModal.show({
      text: `Are you sure you want to delete concept "${name}"?`,
    })
    if (doDelete) {
      try {
        await RestService.delete(`/api/admin/concepts/${id}`)
        ChefService.clearEditConcept()
        LoadMenuConcepts({ ChefService, RestService, pResponseConcepts })()
        UIService.EditMenuConcept.close()
      } catch (error) {
        HandleError({ error })
      }
    }
  }

export const BulkDeleteConcepts =
  ({ ChefService, RestService, UIService, pResponseConcepts, HandleError }) =>
  async (bulkDeleteMap) => {
    try {
      //Validate Concepts are not on any Future Active Group Order
      const { idMap } = await RestService.post(
        `group-orders/date-menus/validate/delete-concepts`,
        { bulkDeleteMap },
      )
      const ids = Object.keys(idMap)
      const gOrderValidationErrs = Object.values(idMap)
        .filter((reason) => reason)
        .join(' & ')
      if (gOrderValidationErrs) {
        UIService.FlashMessage.displayFailureMessage(gOrderValidationErrs)

        return
      } else {
        //Bulk Delete in Rails - rails has validation if concept is on future Catering Order
        const { deletedConcepts, invalidConcepts } = await RestService.delete(
          `/api/admin/concepts/bulk-delete`,
          { ids: ids },
        )
        if (invalidConcepts.length) {
          const invalidConceptReasons = invalidConcepts
            .map((concept) => concept.message.join(', '))
            .join(' & ')
          UIService.FlashMessage.displayFailureMessage(invalidConceptReasons)
        }
        if (deletedConcepts.length) {
          const deletedConceptsNames = deletedConcepts.join(' & ')
          UIService.FlashMessage.displaySuccessMessage(
            `Successfully deleted ${deletedConceptsNames}`,
          )
        }
      }
    } catch (error) {
      HandleError({ error })
    }
    LoadMenuConcepts({ ChefService, RestService, pResponseConcepts })()
  }

export const SaveMenuConcept =
  ({
    ChefService,
    RestService,
    UIService,
    pRequestUpdateMenuConcept,
    pResponseConcepts,
    pRequestUpdateConceptImages,
    HandleError,
  }) =>
  async (data) => {
    UIService.Errors.clear()
    const req = pRequestUpdateMenuConcept(data)
    const headers = { 'Content-Type': 'multipart/form-data', timeout: 30000 }

    try {
      let concept = undefined
      if (data.id) {
        concept = await RestService.put(
          `/api/admin/concepts/${data.id}`,
          req,
          headers,
        )
      } else {
        concept = await RestService.post('/api/admin/concepts', req, headers)
      }

      const imageUpdateReq = pRequestUpdateConceptImages(data)

      if (concept && concept.id && imageUpdateReq) {
        concept = await RestService.put(
          `/api/admin/concepts/${concept.id}`,
          imageUpdateReq,
          headers,
        )
      }

      ChefService.clearEditConcept()
      LoadMenuConcepts({ ChefService, RestService, pResponseConcepts })()
      UIService.EditMenuConcept.close()
    } catch (error) {
      HandleError({ error, namespace: 'menuConceptModal' })
    }
  }

export const GetConceptAvgPricePerPerson =
  ({ RestService, HandleError }) =>
  async (isHappyHour, menuItemIds) => {
    try {
      const avgPPP = await RestService.post(
        '/api/admin/concepts/avg_price_per_person',
        {
          is_happy_hour: isHappyHour,
          menu_item_ids: menuItemIds,
        },
      )

      return camelCaseify(avgPPP).avgPricePerPerson
    } catch (error) {
      HandleError({ error, namespace: 'menuConceptModal' })

      return null
    }
  }

export const GetConceptEstPricePerPerson =
  ({ RestService, HandleError }) =>
  async (mainItemIds, sideItemIds) => {
    try {
      const out = await RestService.post(
        '/api/admin/concepts/estimated_price_per_person',
        {
          main_item_ids: mainItemIds,
          side_item_ids: sideItemIds,
        },
      )

      return camelCaseify(out)
    } catch (error) {
      HandleError({ error, namespace: 'menuConceptModal' })

      return null
    }
  }

export const FetchAndMergeMenuItemModifications =
  ({ RestService, pMergeItemWithMods }) =>
  async (menu) => {
    try {
      const PAGE_SIZE = 50
      const { menuItems } = menu
      const chunks = []

      // Split menu items into chunks of PAGE_SIZE
      for (let i = 0; i < menuItems.length; i += PAGE_SIZE) {
        chunks.push(menuItems.slice(i, i + PAGE_SIZE))
      }

      // Fetch modifications for each chunk in parallel
      const chunkResults = await Promise.all(
        chunks.map((chunk) => {
          const params = new URLSearchParams()
          chunk.forEach((item) => {
            params.append('chefMenuItemIds', item.id)
          })

          return RestService.get(
            `/group-orders/chef-menu-items?${params.toString()}`,
          )
        }),
      )

      // Combine all modification results
      const menuItemMods = chunkResults.flat()

      const menuItemsWithMods = menuItems.map((it) => {
        const modItem =
          menuItemMods.find((modItem) => modItem.id == it.id) || null

        return pMergeItemWithMods(it, modItem)
      })

      return {
        menuWithMods: { ...menu, menuItems: menuItemsWithMods },
        modErrMssg: null,
      }
    } catch (error) {
      return { modErrMssg: error }
    }
  }

export const LoadCuisineTags =
  ({ RestService, HandleError }) =>
  async () => {
    try {
      const resp = await RestService.get('/api/admin/concepts/cuisine_tags')

      return camelCaseify(resp)
    } catch (error) {
      HandleError({ error })
    }
  }

export const LoadGOScheduleCuisines =
  ({ RestService, HandleError }) =>
  async () => {
    try {
      const resp = await RestService.get('/api/admin/concepts/cuisine_tags')

      let cuisines = camelCaseify(resp)
      cuisines.push({ id: 'sandwich', name: 'sandwich' })
      cuisines.push({ id: 'salad', name: 'salad' })
      cuisines = cuisines.sort((a, b) => a.name.localeCompare(b.name))

      return cuisines
    } catch (error) {
      HandleError({ error })
    }
  }
